// Human-skill-as-code: the rubric the planning agent silently self-reviews every
// plan against before finalizing it. It is distilled from Tinuiti's senior-planner
// rubric and maintained in the agent's skill as the authoritative standard — 41
// criteria (11 mechanical + 30 quality). This module surfaces a faithful,
// representative SAMPLE for the demo; the full rubric is the source of truth below.
// Each criterion keeps its real ID and an institutional source attribution, because
// that provenance is the point: this is encoded human judgment, not an algorithm
// minimizing a cost function.

// The Tinuiti Way corpus the quality-tier criteria derive from. These are internal
// Tinuiti documents; we cite them by TITLE in the demo UI (institutional provenance)
// and keep the doc links here for traceability — internal Google Doc URLs are NOT
// surfaced to demo viewers.
//   TVAD Philosophy Guide — docs.google.com/document/d/1wOwYRYNYw_AL4w2AgFcf7ogXZTdcvt-86dpIK-rCK5s
//   Linear TV 201 Deep Dive — .../1fX9HjjzQ5LNBSLNW8Xyy7qX5EIuEn2nv5kqWbZN1tqo
//   YouTube 201 Deep Dive — .../1P87wg0nx38TFHXwEO-F8IC-vw0lk5XIwwXpnSkmlmN4
//   OLV & Display 201 Deep Dive — .../192Ou8bngjOGYmkSkU4Hb01Q5n5K-Is-9Hc-NQSFspa8
//   Digital Audio 201 Deep Dive — .../1r7lE-uzz3R8pS-jsHEwrbHZVjCG5oIo2KSn29eDZsuw
//   TVAD & Commerce Unification — .../12wWm-6X0ejKl7InmQCB-g0V5YTUqOscdUyN9EyID10A
//   1P Data Playbook (One Great Big Unified Playbook for Using 1P Data) — .../1cJeIn87rSAg8WnueX5Uu_AAHtvK8GP2G8GQajj-RPn0
export const TINUITI_WAY_SOURCES: readonly string[] = [
  'TVAD Philosophy Guide',
  'Channel 201 playbooks (Linear TV, YouTube, OLV & Display, Digital Audio)',
  'TVAD & Commerce Unification',
  '1P Data Playbook'
];

// Full rubric size (see source). The panel shows a sample, not all of it.
export const RUBRIC_TOTALS = { mechanical: 11, quality: 30 } as const;
export const RUBRIC_TOTAL = RUBRIC_TOTALS.mechanical + RUBRIC_TOTALS.quality;

export const RubricTier = {
  MECHANICAL: 'mechanical',
  QUALITY: 'quality'
} as const;
export type RubricTierType = (typeof RubricTier)[keyof typeof RubricTier];

export interface RubricCriterion {
  id: string;
  tier: RubricTierType;
  title: string;
  check: string;
  source: string;
  // The capstone criterion — the headline of the "meets the senior-planner bar" story.
  capstone?: boolean;
}

// Representative sample, faithful to the source rubric (real IDs). Each quality-tier
// criterion cites the Tinuiti Way playbook it derives from (see TINUITI_WAY_SOURCES);
// mechanical rules are structural checks, not Tinuiti Way doctrine.
export const RUBRIC_SAMPLE: RubricCriterion[] = [
  {
    id: 'M1',
    tier: RubricTier.MECHANICAL,
    title: 'Budget reconciliation',
    check: 'Line-item spend must reconcile to the total campaign budget within $1.',
    source: 'Mechanical rule'
  },
  {
    id: 'M2',
    tier: RubricTier.MECHANICAL,
    title: 'Weekly minimum per line item',
    check:
      'Every line item must clear $2,500/week — otherwise it is folded into a sibling, reallocated, or dropped so no placement is too thin to matter.',
    source: 'Mechanical rule'
  },
  {
    id: 'Q3',
    tier: RubricTier.QUALITY,
    title: 'Performance channel priority order',
    check:
      'For performance KPIs, CTV and YouTube are must-includes; remaining channels follow a measurability-first order (OLV → Display → Audio → Podcast); Linear only above $1M/mo.',
    source: 'Tinuiti Way · TVAD Philosophy Guide'
  },
  {
    id: 'Q7a',
    tier: RubricTier.QUALITY,
    title: '3P targeting trust hierarchy',
    check:
      'With no 1P data, targeting must draw from the highest-trust signals first — retail-media / purchase & financial-transaction data over demographics, then behavioral, then genre/contextual. Demographic-only targeting is a fail.',
    source: 'Tinuiti Way · 1P Data Playbook'
  },
  {
    id: 'Q7',
    tier: RubricTier.QUALITY,
    title: '1P data usage',
    check:
      'When 1P data exists: suppression must be applied to acquisition line items, retargeting capped at ~$2K/week, and look-alike modeling treated as primary reach — not an afterthought.',
    source: 'Tinuiti Way · 1P Data Playbook'
  },
  {
    id: 'Q4',
    tier: RubricTier.QUALITY,
    title: 'Every premium CPM must earn its premium',
    check:
      'Every line item needs a substantive, intake-linked rationale. Any placement above a $15 CPM (or the plan’s blended CPM) must justify the premium by audience quality, brand safety, or strategic fit — “adds reach” is not enough.',
    source: 'Tinuiti Way · TVAD Philosophy Guide'
  },
  {
    id: 'Q17',
    tier: RubricTier.QUALITY,
    title: 'Strategic coherence: who / what / signals / how',
    check:
      'The plan must answer four questions from its own content: who are we reaching, what do we want them to do, what near/medium/long-term signals tell us it’s working, and how does this specific media accomplish it. Diversity without cohesion is noise.',
    source: 'Tinuiti Way · TVAD Philosophy Guide'
  },
  {
    id: 'Q30',
    tier: RubricTier.QUALITY,
    title: 'Would a senior planner sign off on it?',
    check:
      'A holistic check that the plan meets the bar a senior Tinuiti planner would hold it to — the strategic specificity, rigor, and coherence an expert demands — produced at machine speed and scale, not by an algorithm minimizing a cost function. Includes the “mystery advertiser” test: a reader who didn’t know the client should still be able to identify the brand, its category, and its audience from the plan alone.',
    source: 'Tinuiti Way · TVAD Philosophy Guide',
    capstone: true
  }
];
